/* eslint-disable react-hooks/exhaustive-deps */

import { Paywall } from '@monorepo-hol/design-system';
import { UserInfo } from '@monorepo-hol/providers';

import {
  DetailPage,
  DocumentViewer,
  LoadingState,
  RequestUpgrade,
} from '../../../components';
import { checkIfAuthorized } from '../../../helper/utils';
import { mappingAcl } from '../../../helper/catalog';
import NotFoundPage from '../../../pages/404';
import type { DataDocumentDetail } from '../services/document-detail/types';
import type { SubmenuType } from '../../../components/types';
import { type GetDocumentDetailData } from '../services/document-detail';
import { useTranslationData } from '../hooks/useTranslationData';
import UnexpectedError from '../../../components/ErrorMessage/UnexpectedError/UnexpectedError';
import { MainCatalog } from '../../../types/catalog';
import { useStartTrace } from '../hooks/useStartTrace';
import { PdfProvider } from '../../../provider/PdfProvider';
import type { MetaDataProps } from '../../../types/metadata';
import { type ToolbarProps } from '../../../components/HeaderDetail/Toolbar';
import { useEffect } from 'react';
import { getMetaData } from '../utils';
import { FooterNavPdf } from '../../../components/FooterNavPdf';
import { useDownloadTrigger } from '@pd/hooks/useDownloadTrigger';
import { useDownloadPdf } from '@pd/hooks/useDownloadPdf';

export interface TranslationLayoutProps {
  guid: string;
  user: UserInfo;
  docId?: string;
  dataDocument: GetDocumentDetailData;
  data: DataDocumentDetail;
  subMenu: SubmenuType;
  catalogData?: MainCatalog;
  appendicesList?: any[];
  is404: boolean;
}

export default function TranslationLayout(props: TranslationLayoutProps) {
  const {
    guid,
    user,
    docId,
    dataDocument,
    data,
    subMenu,
    catalogData,
    appendicesList,
    is404,
  } = props;

  const catalogAcl = mappingAcl(data);

  useStartTrace({ guid });

  const { handleDownloadClick, isDownloadAuthorized, isUserLoggedIn } = useDownloadPdf();

  useDownloadTrigger({
    onTriggerDownload: handleDownloadClick,
    enabled: isDownloadAuthorized && isUserLoggedIn,
  });

  const { LangState, isDocumentReady, isDocumentError, pdfDoc } =
    useTranslationData({
      guid,
      data,
      user,
      docId,
      catalogAcl,
      catalogData,
    });

  const isNonLoginOrUserFree: boolean =
    (user && user?.package === 'member') || !user;

  const isToolbarShown =
    !isNonLoginOrUserFree &&
    !(
      catalogAcl?.consolidations !== undefined &&
      !checkIfAuthorized(catalogAcl?.consolidations) &&
      !!user?.jwt
    );

  const renderPaywall = () => {
    if (isNonLoginOrUserFree) {
      return (
        <Paywall
          isUserLoggedIn={user !== undefined}
          additionalQuery={{
            utm_source: 'pusat_data',
            utm_medium: 'pusatdata',
            utm_campaign: 'subscription_paywall',
          }}
          withUtmTerm
          locale={LangState.language}
        />
      );
    }
    if (
      catalogAcl?.translations !== undefined &&
      !checkIfAuthorized(catalogAcl?.translations) &&
      !!user?.jwt
    ) {
      return <RequestUpgrade type="Terjemahan Peraturan" />;
    }
    return null;
  };

  const isNotfound =
    (data !== undefined && Object.keys(data).length === 0) ||
    dataDocument?.status === 404 ||
    !data ||
    !catalogData.mainDocument;

  const condition =
    catalogAcl?.translations !== undefined &&
    checkIfAuthorized(catalogAcl?.translations);

  const isMainDocumentShow = isDocumentReady && pdfDoc?.blob !== undefined;

  const toolbarProps: ToolbarProps = {
    slug: data?.slug,
    lampiranList: appendicesList,
    mainDocumentData: catalogData.mainDocument,
    catalogData,
    smartDocData: undefined,
    pdfData: pdfDoc,
    appendicesType: 'appendices_translation',
    subMenu: 'terjemahan',
  };

  const renderDocumentViewer = () => {
    if (condition) {
      if (isDocumentError) {
        <div className="py-28">
          <UnexpectedError message="Terjadi Kesalahan saat memuat dokumen" />
        </div>;
      } else {
        return (
          <DocumentViewer
            pdfData={pdfDoc}
            smartDocData={undefined}
            subMenu="terjemahan"
            lampiranList={appendicesList}
            appendicesType="appendices_translation"
            mainDocumentData={catalogData.mainDocument}
            isDocumentShowed={isMainDocumentShow}
            catalogData={catalogData}
            slug={data?.slug}
            headerDetailProps={{
              activeMenu: 'translations',
              subMenu,
              metadata: getMetaData(data),
              toolbarProps,
            }}
            is404={is404}
            // activeMenu="translations"
          />
        );
      }
    }
    return null;
  };

  if (isNotfound) {
    return (
      <>
        <NotFoundPage />
        <FooterNavPdf />
      </>
    );
  } else {
    return (
      <PdfProvider catalogData={catalogData}>
        <DetailPage
          data={data}
          subMenu={subMenu}
          activeMenu="translations"
          toolbarProps={toolbarProps}
          isToolbarShown={isToolbarShown && !is404}
        >
          <div>
            {renderDocumentViewer()}

            {renderPaywall()}
          </div>
        </DetailPage>
        <FooterNavPdf />
      </PdfProvider>
    );
  }
}
